/**
 * Servicio para gestionar entrevistas del Hiring Manager (Solicitante)
 * Basado en la documentación del backend
 */

import { esperaDecisionDelSolicitante } from '@/features/rh/lib/rangoDeFechas'
import { config } from '@/config/env'
import type { ColorDeBadge } from '@/shared/components/Badge'
import type {
  HiringManagerInterview,
  HiringManagerInterviewRegistration,
  HiringManagerInterviewDecision,
  FileUploadResponse,
  ApprovedCandidateByArea
} from '@/types/hiringManagerInterview'

class HiringManagerInterviewService {
  private baseUrl: string

  constructor() {
    this.baseUrl = config.apiBaseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const token = localStorage.getItem('access_token')

    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (token) {
      defaultHeaders.Authorization = `Bearer ${token}`
    }

    const requestConfig: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    }

    try {
      const response = await fetch(url, requestConfig)
      
      if (!response.ok) {
        let errorData: any = {}
        try {
          const text = await response.text()
          if (text) {
            errorData = JSON.parse(text)
          }
        } catch {
          // Si no se puede parsear, mantener objeto vacío
        }

        // 🔐 MANEJO DE TOKEN EXPIRADO
        if (response.status === 401) {
          const errorMessage = errorData.message || 'Token expirado o inválido'
          
          if (errorMessage.includes('expirado') || errorMessage.includes('expired')) {
            localStorage.removeItem('access_token')
            localStorage.removeItem('user')
            window.location.href = '/login?session=expired'
            throw new Error('Tu sesión ha expirado. Por favor inicia sesión nuevamente.')
          }
        }

        throw new Error(
          errorData.message || 
          `Error ${response.status}: ${response.statusText}`
        )
      }

      const text = await response.text()
      if (!text) return {} as T
      
      return JSON.parse(text)
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Error de conexión con el servidor')
    }
  }

  /**
   * Crea una nueva entrevista del hiring manager
   * POST /hiring-manager-interviews
   */
  async createInterview(
    data: HiringManagerInterviewRegistration
  ): Promise<HiringManagerInterview> {
    return this.request<HiringManagerInterview>(
      '/hiring-manager-interviews',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Obtiene todas las entrevistas del hiring manager
   * GET /hiring-manager-interviews
   * Filtro opcional por área
   */
  async getAllInterviews(areaId?: number): Promise<HiringManagerInterview[]> {
    let url = '/hiring-manager-interviews'
    
    // Solo agregar parámetro si areaId tiene valor
    if (areaId !== undefined && areaId !== null) {
      url += `?areaId=${areaId}`
    }
    
    return this.request<HiringManagerInterview[]>(url)
  }

  /**
   * Obtiene candidatos aprobados por RH del área del jefe
   * GET /candidates/hr-interview/approved-by-area
   */
  async getApprovedCandidatesByArea(): Promise<ApprovedCandidateByArea[]> {
    return this.request<ApprovedCandidateByArea[]>(
      '/candidates/hr-interview/approved-by-area'
    )
  }

  /**
   * Obtiene una entrevista específica por ID
   * GET /hiring-manager-interviews/:id
   */
  async getInterviewById(id: number): Promise<HiringManagerInterview> {
    return this.request<HiringManagerInterview>(
      `/hiring-manager-interviews/${id}`
    )
  }

  /**
   * Registra la decisión del hiring manager (OBLIGATORIA)
   * PATCH /hiring-manager-interviews/:id/decision
   * 
   * Si aprueba: candidato → SELECCIONADO
   * Si rechaza: candidato → ENTREVISTA_RH
   */
  async recordDecision(
    id: number,
    decision: HiringManagerInterviewDecision
  ): Promise<HiringManagerInterview> {
    return this.request<HiringManagerInterview>(
      `/hiring-manager-interviews/${id}/decision`,
      {
        method: 'PATCH',
        body: JSON.stringify(decision),
      }
    )
  }

  /**
   * Elimina una entrevista
   * DELETE /hiring-manager-interviews/:id
   */
  async deleteInterview(id: number): Promise<void> {
    return this.request<void>(
      `/hiring-manager-interviews/${id}`,
      {
        method: 'DELETE',
      }
    )
  }

  /**
   * Sube el archivo de resultados de la entrevista
   * Similar a uploadTestFile de HR Interview
   */
  async uploadResultsFile(interviewId: number, file: File): Promise<FileUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const token = localStorage.getItem('access_token')
    const headers: HeadersInit = {}
    
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/hiring-manager-interviews/${interviewId}/upload-results`,
        {
          method: 'POST',
          headers,
          body: formData,
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Error al subir archivo: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error al subir archivo:', error)
      throw error
    }
  }

  /**
   * Obtiene candidatos listos para entrevista con el jefe
   * Estado: pre_seleccionado
   */
  async getCandidatesReadyForInterview(): Promise<any[]> {
    const token = localStorage.getItem('access_token')
    const response = await fetch(
      `${this.baseUrl}/candidates?status=pre_seleccionado`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )

    if (!response.ok) {
      throw new Error('Error al obtener candidatos')
    }

    return response.json()
  }

  /**
   * Obtiene entrevistas pendientes de decisión
   */
  getPendingDecisions(interviews: HiringManagerInterview[]): HiringManagerInterview[] {
    // La misma regla que la lista usa para decir «Pendiente» y ofrecer aprobar o
    // rechazar (SRH-331). Contar solo `decision === 'pendiente'` sumaba
    // entrevistas de candidatos que ya siguieron su camino: el aviso decía dos
    // y la lista mostraba una.
    return interviews.filter(esperaDecisionDelSolicitante)
  }

  /**
   * Obtiene entrevistas completadas
   */
  getCompletedInterviews(interviews: HiringManagerInterview[]): HiringManagerInterview[] {
    return interviews.filter(interview => 
      interview.decision === 'aprobado' || interview.decision === 'rechazado'
    )
  }

  /**
   * Valida los datos de registro de entrevista
   */
  validateInterviewRegistration(
    data: HiringManagerInterviewRegistration
  ): { isValid: boolean; errors: string[] } {
    const errors: string[] = []

    if (!data.candidateId) {
      errors.push('El candidato es requerido')
    }

    if (!data.fechaEntrevista) {
      errors.push('La fecha de entrevista es requerida')
    } else {
      // Validar formato de fecha YYYY-MM-DD
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/
      if (!dateRegex.test(data.fechaEntrevista)) {
        errors.push('La fecha debe tener el formato YYYY-MM-DD')
      }
    }

    if (!data.horaEntrevista) {
      errors.push('La hora de entrevista es requerida')
    } else {
      // Validar formato de hora HH:mm
      const timeRegex = /^\d{2}:\d{2}$/
      if (!timeRegex.test(data.horaEntrevista)) {
        errors.push('La hora debe tener el formato HH:mm')
      }
    }

    if (!data.modalidad) {
      errors.push('La modalidad es requerida')
    } else if (data.modalidad !== 'presencial' && data.modalidad !== 'virtual') {
      errors.push('La modalidad debe ser "presencial" o "virtual"')
    }

    if (!data.entrevistadorId) {
      errors.push('El entrevistador es requerido')
    }

    // Validar que si es virtual, debe tener ubicacionLink
    if (data.modalidad === 'virtual' && !data.ubicacionLink) {
      errors.push('Para entrevistas virtuales es requerido el link de la reunión')
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  /**
   * Valida la decisión del hiring manager
   */
  validateDecision(
    decision: HiringManagerInterviewDecision
  ): { isValid: boolean; errors: string[] } {
    const errors: string[] = []

    if (!decision.decision) {
      errors.push('La decisión es requerida')
    } else if (decision.decision !== 'aprobado' && decision.decision !== 'rechazado') {
      errors.push('La decisión debe ser "aprobado" o "rechazado"')
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  /**
   * Formatea la fecha y hora para mostrar
   */
  formatInterviewDateTime(fecha: string, hora: string): string {
    try {
      const date = new Date(fecha + 'T' + hora)
      return new Intl.DateTimeFormat('es-MX', {
        dateStyle: 'full',
        timeStyle: 'short'
      }).format(date)
    } catch {
      return `${fecha} ${hora}`
    }
  }

  /**
   * Obtiene el label de la decisión
   */
  getDecisionLabel(decision: 'aprobado' | 'rechazado' | 'pendiente'): string {
    const labels = {
      aprobado: 'Aprobado',
      rechazado: 'Rechazado',
      pendiente: 'Pendiente'
    }
    return labels[decision] || decision
  }

  /**
   * Obtiene el color del Badge para la decisión
   */
  getDecisionColor(decision: 'aprobado' | 'rechazado' | 'pendiente'): ColorDeBadge {
    const colors: Record<string, ColorDeBadge> = {
      aprobado: 'verde',
      rechazado: 'rojo',
      pendiente: 'amarillo'
    }
    return colors[decision] || 'gris'
  }
}

// Export singleton instance
export const hiringManagerInterviewService = new HiringManagerInterviewService()
